import React, { Component } from 'react';
import connectToParent from 'penpal/lib/connectToParent';
import DocumentViewer, { getUrl, getType } from './documentViewer';
import { documentTemplateTabs, inIframe } from './utils';

export const HEIGHT_OFFSET = 60; // Height offset to prevent double scrollbar in certain browsers

const VERSION = 'v0.9.0.6';

class DocumentViewerContainer extends Component {
  constructor(props) {
    super(props);

    this.handleDocumentChange = this.handleDocumentChange.bind(this);
    this.selectTemplateTab = this.selectTemplateTab.bind(this);
    this.updateParentHeight = this.updateParentHeight.bind(this);
    this.updateParentTemplateTabs = this.updateParentTemplateTabs.bind(this);
    this.handleObfuscation = this.handleObfuscation.bind(this);
    this.handleIframeMessage = this.handleIframeMessage.bind(this);
    this.state = {
      parentFrameConnection: null,
      document: null,
      tabIndex: 0,
    };
    this.print = this.print.bind(this);
    this.iframeHeight = 0; // Lưu trữ chiều cao nhận được từ iframe
    this.externalTemplateRef = React.createRef();
  }

  updateHeightWhenResize() {
    window.addEventListener('resize', this.updateParentHeight);
  }

  // Use postMessage to update iframe's parent to scale the height
  async updateParentHeight() {
    if (inIframe()) {
      const { parentFrameConnection } = this.state;
      const parent = await parentFrameConnection;
      if (parent.updateHeight) {
        // Nếu đã nhận được chiều cao thực từ external iframe, sử dụng nó.
        // Ngược lại sử dụng offsetHeight của document hiện tại hoặc fallback 650.
        let height =
          this.iframeHeight > 0
            ? Math.max(this.iframeHeight + HEIGHT_OFFSET, 650)
            : Math.max(document.documentElement.offsetHeight, 650);
        await parent.updateHeight(height);
      }
    }
  }

  async sendCertificatesInfo(certsInfo) {
    if (certsInfo.length === 0) return;
    const { parentFrameConnection } = this.state;
    const parent = await parentFrameConnection;
    if (!parent || !parent.setCertificatesInfo) {
      return;
    }
    await parent.setCertificatesInfo(certsInfo);
  }

  async updateCertInfo() {
    const pages = document.querySelectorAll('.page');
    if (pages.length === 0) return;

    const certsInfo = Array.from(pages).map((page, index) => {
      const headingElement = page.querySelector('.legal-terms-heading');

      return {
        pageNumber: index + 1,
        name: headingElement
          ? headingElement.textContent.trim()
          : `Certificate ${index + 1}`,
      };
    });

    await this.sendCertificatesInfo(certsInfo);
  }

  // Xử lý message từ external iframe gửi lên (kèm HTML & kích thước)
  async handleIframeMessage(event) {
    const data = event.data;
    if (data && data.type === 'IFRAME_FULL_HTML') {
      const { html, height } = data;

      this.iframeHtml = html; // Lưu trữ HTML thô để dùng cho chức năng in (print)

      // Cập nhật lại iframeHeight của class để đồng bộ khi resize
      if (height && height > 0) {
        this.iframeHeight = height;
        this.updateParentHeight();
      }

      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        this.iframeDoc = doc; // Lưu trữ doc ảo để dùng khi in
        const pages = doc.querySelectorAll('.page');

        if (pages.length > 0) {
          const certsInfo = Array.from(pages).map((page, index) => {
            const headingElement = page.querySelector('.legal-terms-heading');

            return {
              pageNumber: index + 1,
              name: headingElement
                ? headingElement.textContent.trim()
                : `Certificate ${index + 1}`,
            };
          });

          await this.sendCertificatesInfo(certsInfo);
        }
      } catch (error) {
        console.error('Error parsing external iframe HTML:', error);
      }
    }
  }

  // Use postMessage to update iframe's parent on the selection of templates available for this document
  async updateParentTemplateTabs() {
    const templates = await documentTemplateTabs(this.state.document);
    this.setState({ templates });
    if (inIframe()) {
      const { parentFrameConnection } = this.state;
      const parent = await parentFrameConnection;
      if (parent.updateTemplates) {
        await parent.updateTemplates(documentTemplateTabs(this.state.document));
      }
    }
  }

  selectTemplateTab(tabIndex) {
    this.setState({ tabIndex }, () => this.updateCertInfo());
  }

  handleDocumentChange(document) {
    this.setState({ document }, () => {
      this.updateParentTemplateTabs();
      this.updateCertInfo();
    });
  }

  async handleObfuscation(field) {
    if (inIframe()) {
      const { parentFrameConnection } = this.state;
      const parent = await parentFrameConnection;
      if (parent.handleObfuscation) {
        parent.handleObfuscation(field);
      }
    }
  }

  print(pageNumber = 1) {
    if (!this.state.document) return;

    if (this.iframeDoc) {
      if (this.externalTemplateRef.current) {
        this.externalTemplateRef.current.print(pageNumber);
      }
      return;
    } else if (this.externalTemplateRef.current) {
      this.externalTemplateRef.current.print(0);
    } else {
      window.print();
    }
  }

  componentDidUpdate() {
    this.updateParentHeight();
  }

  componentDidMount() {
    window.addEventListener('message', this.handleIframeMessage);

    const renderDocument = this.handleDocumentChange;
    const selectTemplateTab = this.selectTemplateTab;
    if (inIframe()) {
      const parentFrameConnection = connectToParent({
        methods: {
          renderDocument,
          selectTemplateTab,
          print: this.print,
        },
        childOrigin: '*',
      }).promise;
      this.setState({ parentFrameConnection });
    }
    this.updateHeightWhenResize();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateParentHeight);
    window.removeEventListener('message', this.handleIframeMessage);
  }

  render() {
    if (!this.state.document) {
      return (
        <div style={{ position: 'relative', height: '100vh' }}>
          <p
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              fontSize: 11,
              display: 'none',
            }}
          >
            {VERSION}
          </p>
        </div>
      );
    }
    return (
      <DocumentViewer
        document={this.state.document}
        tabIndex={this.state.tabIndex}
        handleHeightUpdate={this.updateParentHeight}
        handleObfuscation={this.handleObfuscation}
        externalTemplateRef={this.externalTemplateRef}
      />
    );
  }
}
export default DocumentViewerContainer;
